import { JOB_TYPE_FAMILIES, type JobTypeInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, Select } from 'antd';
import { useEffect } from 'react';
import type { JobType } from './reference-data.api';

interface JobTypeFormDrawerProps {
  open: boolean;
  /** Present to edit that row; absent to create a new one. */
  editing?: JobType;
  /** Selectable options — active document types, plus (when editing) any it already requires, even if since deactivated, so its label still shows. */
  requiredDocumentTypes: { code: string; name: string }[];
  error: string | null;
  submitting: boolean;
  onSubmit: (input: JobTypeInput) => Promise<boolean>;
  onClose: () => void;
}

const FAMILY_OPTIONS = JOB_TYPE_FAMILIES.map((value) => ({ value, label: value[0].toUpperCase() + value.slice(1) }));

/** One drawer for both create and edit — "edit every field" means there's no separate patch shape. */
export function JobTypeFormDrawer({ open, editing, requiredDocumentTypes, error, submitting, onSubmit, onClose }: JobTypeFormDrawerProps) {
  const [form] = Form.useForm<JobTypeInput>();

  useEffect(() => {
    if (open) {
      form.setFieldsValue(
        editing
          ? { code: editing.code, name: editing.name, family: editing.family, requiredDocumentTypeCodes: editing.requiredDocuments.map((d) => d.code) }
          : { code: '', name: '', family: 'container', requiredDocumentTypeCodes: [] },
      );
    }
  }, [open, editing, form]);

  async function submit(values: JobTypeInput) {
    const saved = await onSubmit(values);
    if (saved) onClose();
  }

  const documentOptions = requiredDocumentTypes.map((d) => ({ value: d.code, label: d.name }));

  return (
    <Drawer open={open} onClose={onClose} title={editing ? 'Edit job type' : 'New job type'} placement="right" size={420} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<JobTypeInput> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Enter a code' }]}>
          <Input className="font-mono" autoFocus />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="family" label="Cargo attributes" rules={[{ required: true, message: 'Choose which cargo attribute set applies' }]}>
          <Select options={FAMILY_OPTIONS} />
        </Form.Item>
        <Form.Item name="requiredDocumentTypeCodes" label="Required documents">
          <Select mode="multiple" options={documentOptions} placeholder="None" />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {editing ? 'Save' : 'Create job type'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
